// Cart upsell slider + one-tap add (also used by the "Resize it at home" box on product pages).
// Adds go through the theme's CartLinesUpdateEvent, so the drawer opens / re-renders like a normal add to cart.
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const recCache = new Map();
/** Shopify recommendations (related + complementary) for one product, cached per page view. */
function recommendationsFor(id) {
  if (!recCache.has(id)) {
    const base = `${(window.Shopify?.routes?.root || '/').replace(/\/$/, '')}/recommendations/products.json?product_id=${id}&limit=10&intent=`;
    recCache.set(
      id,
      Promise.all(
        ['related', 'complementary'].map((intent) =>
          fetch(base + intent)
            .then((r) => (r.ok ? r.json() : { products: [] }))
            .then((j) => j.products || [])
            .catch(() => [])
        )
      ).then(([a, b]) => a.concat(b))
    );
  }
  return recCache.get(id);
}

function money(cents) {
  const cur = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

class MrCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.list = this.querySelector('.mr-upsell__list');
    if (!this.list) return;
    this.list.addEventListener('scroll', this.#syncArrows, { passive: true });
    this.ro = new ResizeObserver(this.#syncArrows);
    this.ro.observe(this.list);
    this.#addRecommendations();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.ro?.disconnect();
  }

  #syncArrows = () => {
    const nav = this.querySelector('.mr-upsell__nav');
    if (!nav || !this.list) return;
    const max = this.list.scrollWidth - this.list.clientWidth;
    nav.hidden = max < 4;
    const [prev, next] = nav.querySelectorAll('button');
    prev.disabled = this.list.scrollLeft < 4;
    next.disabled = this.list.scrollLeft > max - 4;
  };

  async #addRecommendations() {
    const inCart = this.dataset.inCart || ',';
    const ids = inCart.split(',').filter(Boolean);
    if (!ids.length) return;
    const lists = await Promise.all([...new Set(ids)].map(recommendationsFor));
    if (!this.isConnected) return;
    const have = new Set([...this.list.querySelectorAll('.mr-upsell__item')].map((li) => li.dataset.productId));
    const recs = [];
    for (const p of lists.flat()) {
      const id = String(p.id);
      if (!p.available || have.has(id) || inCart.includes(`,${id},`) || recs.length >= 10) continue;
      have.add(id);
      recs.push(p);
    }
    if (!recs.length) return;
    const anchor = this.list.querySelector('.mr-upsell__item[data-src="base"]');
    for (const p of recs) this.list.insertBefore(this.#item(p), anchor);
    // keep the slider at its maximum length: drop collection fillers from the end
    const max = Number(this.dataset.max || 12);
    [...this.list.querySelectorAll('.mr-upsell__item')].slice(max).forEach((li) => li.remove());
    this.#syncArrows();
  }

  /** @param {any} p */
  #item(p) {
    const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
    const li = document.createElement('li');
    li.className = 'mr-upsell__item';
    li.dataset.productId = String(p.id);
    li.dataset.src = 'rec';
    const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=120&height=120&crop=center` : '';
    const free = this.dataset.freeLabel;
    const price = v?.price ?? p.price;
    const compare = v?.compare_at_price ?? p.compare_at_price;
    const link = (cls) => {
      const el = document.createElement('a');
      el.href = p.url;
      el.className = cls;
      return el;
    };
    const imgLink = link('mr-upsell__img');
    imgLink.tabIndex = -1;
    if (img) {
      const im = document.createElement('img');
      Object.assign(im, { src: img, width: 56, height: 56, loading: 'lazy', alt: p.title });
      imgLink.append(im);
    }
    const info = document.createElement('div');
    info.className = 'mr-upsell__info';
    const title = link('mr-upsell__title');
    title.textContent = p.title;
    const pr = document.createElement('span');
    pr.className = 'mr-upsell__price';
    if (compare > price) {
      const s = document.createElement('s');
      s.textContent = money(compare);
      pr.append(s);
    }
    const strong = document.createElement('strong');
    strong.textContent = price === 0 && free ? free : money(price);
    pr.append(strong);
    info.append(title, pr);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mr-upsell__add';
    btn.dataset.variantId = String(v?.id || '');
    btn.textContent = '+ Add';
    btn.setAttribute('aria-label', `Add ${p.title}`);
    li.append(imgLink, info, btn);
    return li;
  }

  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const arrow = target?.closest('.mr-upsell__arrow');
    if (arrow && this.list) {
      const first = this.list.querySelector('.mr-upsell__item');
      const step = first ? first.getBoundingClientRect().width + 16 : this.list.clientWidth;
      this.list.scrollBy({ left: Number(arrow.getAttribute('data-dir')) * step, behavior: 'smooth' });
      return;
    }
    const button = target?.closest('.mr-upsell__add');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    const label = button.textContent;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const id = button.dataset.variantId;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(','), sections_url: location.pathname });
      const added = await fetch(Theme.routes.cart_add_url, { ...fetchConfig('json', { body }) }).then((r) => r.json());
      if (added.status) throw new Error(added.message || 'Add failed');
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'mr-cart-upsell', sections: added.sections, didError: false },
      });
      if (button.isConnected) {
        // buttons outside the re-rendered cart (product page box) show a short confirmation, then reset
        button.textContent = 'Added ✓';
        setTimeout(() => {
          button.textContent = label;
          button.removeAttribute('aria-busy');
        }, 2500);
      }
    } catch (error) {
      deferred.reject(error);
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('mr-cart-upsell')) customElements.define('mr-cart-upsell', MrCartUpsell);
